/**
 * 分片上传：白板图片与文件传输共用。
 *
 * 走 WebSocket 的二进制帧（见 docs/03-protocol.md 第 2 节），
 * 而不是单独开 HTTP 上传 —— 这样服务端能把上传与房间上下文关联，
 * 不必再为上传单独做一套鉴权。
 */

import type { Client } from './client'
import { BinKind } from './messages'

/** 单个分片大小。
 *
 * 取 48KB：服务端单帧上限是 60KB（对应老系统 MAX_PACKLEN），
 * 留出 JSON 头部与 WebSocket 帧头的余量。 */
const CHUNK_SIZE = 48 * 1024

export interface UploadProgress {
  sent: number
  total: number
  /** 0-1 */
  ratio: number
}

/**
 * 把数据分片发出去。
 *
 * 分片之间让出事件循环：一次性把几十个分片塞进 WebSocket 会让
 * 发送缓冲暴涨，页面在上传大文件时肉眼可见地卡顿。
 */
export async function uploadChunks(
  client: Client,
  kind: number,
  header: Record<string, unknown>,
  data: Uint8Array,
  onProgress?: (p: UploadProgress) => void,
): Promise<void> {
  const total = Math.max(1, Math.ceil(data.length / CHUNK_SIZE))

  for (let seq = 0; seq < total; seq++) {
    const start = seq * CHUNK_SIZE
    const chunk = data.subarray(start, Math.min(start + CHUNK_SIZE, data.length))
    // 连接断了就立刻失败：继续往下发只是把后面的分片也悄悄丢掉，
    // 界面上进度条照样走到头，对方却永远等不到完整的文件。
    if (!client.sendBinary(kind, { ...header, seq, total }, chunk)) {
      throw new Error('连接已断开，传输中止')
    }

    onProgress?.({
      sent: Math.min(start + chunk.length, data.length),
      total: data.length,
      ratio: (seq + 1) / total,
    })

    // 每片之间让出一次，避免长时间占住主线程。
    if (seq % 4 === 3) await new Promise((r) => setTimeout(r, 0))
  }
}

/** 上传一张白板图片。objId 由服务端在图元创建时分配。 */
export async function uploadWhiteboardImage(
  client: Client,
  objId: number,
  page: number,
  blob: Blob,
  onProgress?: (p: UploadProgress) => void,
): Promise<void> {
  const buf = new Uint8Array(await blob.arrayBuffer())
  await uploadChunks(
    client,
    BinKind.WhiteboardBlob,
    { objId, page, mime: blob.type || 'image/png' },
    buf,
    onProgress,
  )
}

/** 上传一个传输中的文件。 */
export async function uploadFile(
  client: Client,
  fileId: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
): Promise<void> {
  const buf = new Uint8Array(await file.arrayBuffer())
  await uploadChunks(
    client,
    BinKind.FileChunk,
    { fileId, mime: file.type || 'application/octet-stream' },
    buf,
    onProgress,
  )
}

/** 生成一个本地唯一的传输标识。 */
export function newFileID(): string {
  const rnd = crypto.getRandomValues(new Uint32Array(2))
  return `f${Date.now().toString(36)}${rnd[0]!.toString(36)}${rnd[1]!.toString(36)}`
}

/** 把 Canvas 转成 Blob。 */
export function canvasToBlob(canvas: HTMLCanvasElement, type = 'image/png'): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (b) resolve(b)
      else reject(new Error('画布导出失败'))
    }, type, 0.92)
  })
}
